// What "matching" means for each kind of record, in one place.
//
// Lives in shared/ (not client/) because matching is domain logic, not UI: the
// server matches new listings against open buyer requests using these exact
// functions. A server-side re-implementation would be the two-matchers bug
// (trap #40) with a network boundary hiding it.
//
// The typeahead and the browse tabs MUST agree. They didn't: the dropdown used
// the real matcher while the Listings tab still did `name.includes(query)`, so
// searching "Dessert" offered ten results and then landed on "No items found".
// Anything that filters items, makers or boxes builds its fields here.

import { fuzzyScoreFields, type SearchField } from "./fuzzy.js";

export type SearchableSeller = {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
} | null | undefined;

export type SearchableItem = {
  name: string;
  sellerId?: string | null;
  category?: string | null;
  isGlutenFree?: number | null;
  seller?: SearchableSeller;
};

export type SearchableProducer = {
  id: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  sellerType?: string | null;
  zipCode?: string | null;
};

export type SearchableBox = {
  title: string;
  sellerNames: string[];
  items: { name: string }[];
};

export function sellerLabel(s: SearchableSeller): string {
  if (!s) return "";
  return s.displayName || [s.firstName, s.lastName].filter(Boolean).join(" ") || "";
}

export function producerLabel(p: SearchableProducer): string {
  return (
    p.displayName || [p.firstName, p.lastName].filter(Boolean).join(" ") || "Local maker"
  );
}

// Dietary wording people search for ("vegan", "gluten free") that is otherwise
// invisible: isGlutenFree is a real column nothing was searching, and makers
// mark diet in the title as "(V)" / "(GF/V)" rather than in a field.
export function dietaryText(i: SearchableItem): string {
  const tags: string[] = [];
  if (Number(i.isGlutenFree) === 1) tags.push("gluten", "free", "glutenfree", "gf");
  const marks = (i.name.match(/\(([^)]+)\)/g) ?? []).join(" ").toLowerCase();
  if (marks) {
    if (/\bv\b/.test(marks) || marks.includes("vegan")) tags.push("vegan", "plant", "based");
    if (/\bgf\b/.test(marks) || marks.includes("gluten")) {
      tags.push("gluten", "free", "glutenfree", "gf");
    }
    if (marks.includes("df") || marks.includes("dairy")) tags.push("dairy", "free");
  }
  // Category-level facts, not seller claims: whole produce IS vegan and
  // gluten-free whether or not the title says so — "vegan" must return the
  // lettuce and the tomatoes, not only items marked (V). Kept conservative:
  // pantry/prepared can hide anything (honey, meat sauces), garden items are
  // live plants rather than food, so neither gets inferred tags.
  const cat = (i.category ?? "").toLowerCase();
  if (cat === "produce") {
    tags.push("vegan", "vegetarian", "plant", "based", "gluten", "free", "glutenfree", "gf");
  } else if (cat === "eggs" || cat === "eggs_dairy") {
    tags.push("vegetarian", "gluten", "free", "glutenfree", "gf");
  }
  return tags.join(" ");
}

export function itemFields(i: SearchableItem): SearchField[] {
  return [
    [i.name, 1],
    [sellerLabel(i.seller), 0.4],
    [i.category ?? "", 0.35, "strict"],
    [dietaryText(i), 0.6, "strict"],
  ];
}

// What each maker sells, so searching a product surfaces the people who have
// it. Built once per list, not per record.
export type ProducerIndex = {
  sells: Map<string, string[]>;
  cats: Map<string, Set<string>>;
  diet: Map<string, Set<string>>;
};

export function buildProducerIndex(items: (SearchableItem & { sellerId?: string | null })[]): ProducerIndex {
  const sells = new Map<string, string[]>();
  const cats = new Map<string, Set<string>>();
  const diet = new Map<string, Set<string>>();
  for (const i of items) {
    const id = i.sellerId;
    if (!id) continue;
    const list = sells.get(id);
    if (list) list.push(i.name);
    else sells.set(id, [i.name]);
    if (i.category) {
      const set = cats.get(id);
      if (set) set.add(i.category);
      else cats.set(id, new Set([i.category]));
    }
    const d = dietaryText(i);
    if (d) {
      const set = diet.get(id);
      if (set) d.split(" ").forEach((x) => set.add(x));
      else diet.set(id, new Set(d.split(" ")));
    }
  }
  return { sells, cats, diet };
}

// Deliberately no bio. It's marketing prose, so some word in it is always close
// to the query, and makers kept surfacing for things they don't sell ("cake"
// hit a bio that said "we make..."). Relevance comes from the name and the
// actual inventory.
export function producerFields(p: SearchableProducer, idx: ProducerIndex): SearchField[] {
  return [
    [producerLabel(p), 1],
    [(idx.sells.get(p.id) ?? []).join(" "), 0.9],
    [[...(idx.cats.get(p.id) ?? [])].join(" "), 0.5, "strict"],
    [[...(idx.diet.get(p.id) ?? [])].join(" "), 0.45, "strict"],
    [p.sellerType ?? "", 0.4, "strict"],
    [p.zipCode ?? "", 0.2, "strict"],
  ];
}

export function boxFields(b: SearchableBox): SearchField[] {
  return [
    [b.title, 1],
    [b.sellerNames.join(" "), 0.5],
    [b.items.map((it) => it.name).join(" "), 0.6],
  ];
}

export function matchesItem(query: string, i: SearchableItem): boolean {
  return fuzzyScoreFields(query, itemFields(i)) != null;
}

// Vocabulary from the AI assist layer (server/search-assist.ts), applied
// through this same matcher rather than as its own scoring path — the model
// widens WHAT we look for, it never changes what "matches" means. Terms are
// alternatives (any one qualifies); dietary tokens are requirements (all must
// hold), so "vegan picnic food" can't return the ham sandwich.
export type SearchAssist = { terms: string[]; dietary: string[]; categories: string[] };

export function matchesAssist(assist: SearchAssist, i: SearchableItem): boolean {
  // Categories scope the whole expansion. Makers name candles and crafts after
  // foods, so without this a "sandwich" search matched a "Lemon Loaf" soy
  // candle through the term "loaf" — the term is right, the domain is wrong.
  if (assist.categories.length > 0) {
    const cat = (i.category ?? "").trim().toLowerCase();
    if (!assist.categories.includes(cat)) return false;
  }
  if (assist.dietary.length > 0) {
    const tags = new Set(dietaryText(i).split(" ").filter(Boolean));
    if (!assist.dietary.every((tag) => tags.has(tag))) return false;
  }
  if (assist.terms.length > 0) return assist.terms.some((term) => matchesItem(term, i));
  // No terms: a category-shaped query ("ready made items" -> prepared) or a
  // pure dietary one. Anything that passed the constraints above qualifies.
  return assist.categories.length > 0 || assist.dietary.length > 0;
}

export function matchesProducer(query: string, p: SearchableProducer, idx: ProducerIndex): boolean {
  return fuzzyScoreFields(query, producerFields(p, idx)) != null;
}

export function matchesBox(query: string, b: SearchableBox): boolean {
  return fuzzyScoreFields(query, boxFields(b)) != null;
}
